/**
 *
 * Attendance
 *
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import moment, { localeFr } from 'utils/moment/momentLocale';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  flushStateAttendance,
  loadMobileCourses,
  updateCourse,
  loadCourse,
} from './actions';

import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemsList,
  makeSelectCourse,
  makeSelectLoadingUpdates,
  makeSelectLoadingCourse
} from './selectors';

// antd component
import { Layout } from 'antd';
const { Content } = Layout;

// components
import PresencesListCourses from 'mobile/components/PresencesListCourses';
import TCFCalendar from 'components/TCFCalendar';
// containers
import AttendanceControl from 'mobile/containers/AttendanceControl';

// global user
import { useGetLocalUser } from 'engine/Contexts/User.context';

// utils
import { DAY_OF_WEEK } from 'utils/helpers/helper.constant';
import { getObjId } from 'utils/helpers/helper.general';


export function Attendance({
  // main data
  courseDetail,
  itemsList,
  // states
  error,
  loading,
  loadingUpdates,
  loadingCourse,
  // actions
  loadCourse,
  loadMobileCourses,
  updateCourse,
  flushState,
}) {
  useInjectReducer({ key: 'attendance', reducer });
  useInjectSaga({ key: 'attendance', saga });

  const now = moment();
  const week = now.isoWeekday();
  const localUser = useGetLocalUser()();

  let initOptions = {
    _id: localUser?._id,
    course_week: DAY_OF_WEEK[week - 1]?.value,
  };

  const [selectedDate, setSelectedDate] = useState(now);
  const [optionsData, setOptionsData] = useState(initOptions);
  const [currentCourses, setCurrentCourses] = useState([]);
  // for AttendanceControl
  const [currentCourse, setCurrentCourse] = useState(false);
  const [checkedCourse, setCheckedCourse] = useState(false);


  // Clearing the state after unmounting a component
  // - work like ComponentDidUpdate
  useEffect(() => {
    itemsList?.length == 0 && loadMobileCourses(optionsData);
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  useEffect(() => {
    courseDetail && setCurrentCourse(courseDetail);
  }, [courseDetail]);

  useEffect(() => {
    if (itemsList) {
      setCurrentCourses(itemsList);
    }
  }, [itemsList]);

  // Loading data when optionsData is changed
  const changeLoadOptions = useCallback(
    newOption => {
      if (JSON.stringify(optionsData) != JSON.stringify(newOption)) {
        const options = {
          ...optionsData,
          ...newOption,
        };
        loadMobileCourses(options);
        setOptionsData(options);
      }
    },
    [optionsData],
  );

  const onCalendarSelect = useCallback(value => {
    setSelectedDate(moment(value));
    // 1 - Monday, 7 - Sunday
    const week2 = value.isoWeekday();
    changeLoadOptions({
      course_week: DAY_OF_WEEK[week2 - 1]?.value,
    });
  }, []);

  const updateCourseHandle = useCallback(
    courseData => {
      setCheckedCourse(courseData);
      setCurrentCourse({
        ...currentCourse,
        ...courseData
      });
      if (!!courseData) {
        const updatedCourses = currentCourses?.map(uc => {
          if (getObjId(uc) == getObjId(courseData)) {
            return courseData;
          } else {
            return uc;
          }
        });
        setCurrentCourses(updatedCourses);
      }
    },
    [currentCourses],
  );

  const checkCourse = useCallback( checkedCourseID => {
    checkedCourseID && loadCourse(checkedCourseID);
  }, [loadCourse] );

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>Attendance</title>
        <meta name="description" content="Description of Attendance" />
      </Helmet>
      <Content className="main-content-container attendance-page">
        <TCFCalendar
          valueState={selectedDate}
          onSelect={onCalendarSelect}
          mode="day"
        />
        <div className="attendance-page__main">
          <PresencesListCourses
            className="courses-list-wrapper"
            selectedDate={moment(selectedDate).toISOString()}
            dataSourse={currentCourses}
            loading={loading}
            checkedCourse={checkedCourse}
            currentCourse={currentCourse}
            getCourse={checkCourse}
          />

          {!loading && (currentCourses && currentCourses.length > 0) && (
            <AttendanceControl
              course={currentCourse}
              onDate={moment(selectedDate).toISOString()}
              loadingUpdates={loadingUpdates || loadingCourse}
              updateCourse={updateCourseHandle}
            />
          )}
        </div>
      </Content>
    </Layout>
  );
}

Attendance.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  loadingCourse: makeSelectLoadingCourse(),
  loadingUpdates: makeSelectLoadingUpdates(),
  error: makeSelectError(),
  itemsList: makeSelectItemsList(),
  courseDetail: makeSelectCourse(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateAttendance()),
    loadMobileCourses: options => dispatch(loadMobileCourses(options)),
    updateCourse: itemData => dispatch(updateCourse(itemData)),
    loadCourse: courseID => dispatch(loadCourse(courseID)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo
)(Attendance);
